import { Button } from "@/components/ui/button"
import * as React from "react"

type ErrorBannerProps = {
	readonly title: string
	readonly message: string
	readonly onRetry?: () => void
}

export function ErrorBanner({ title, message, onRetry }: ErrorBannerProps) {
	const showRetry = typeof onRetry === "function"
	return (
		<div className="rounded-xl border border-destructive/40 bg-destructive/10 p-4 text-body text-destructive flex items-center justify-between gap-4">
			<div>
				<p className="font-semibold">{title}</p>
				<p className="text-destructive/80">{message}</p>
			</div>
			{showRetry ? (
				<Button size="sm" variant="secondary"
					type="button"
					onClick={onRetry}
					className="shrink-0"
				>
					Retry
				</Button>
			) : null}
		</div>
	)
}

type ErrorBoundaryProps = {
	readonly children: React.ReactNode
	readonly fallback?: React.ReactNode | ((reset: () => void) => React.ReactNode)
	readonly onReset?: () => void
	readonly resetKey?: string | number
	readonly title?: string
}

type ErrorBoundaryState = {
	readonly hasError: boolean
	readonly error: Error | null
}

export class ErrorBoundary extends React.Component<
	ErrorBoundaryProps,
	ErrorBoundaryState
> {
	public state: ErrorBoundaryState = { hasError: false, error: null }

	public static getDerivedStateFromError(error: Error): ErrorBoundaryState {
		return { hasError: true, error }
	}

	public componentDidUpdate(prevProps: ErrorBoundaryProps) {
		if (!this.state.hasError) return
		const resetKey = this.props.resetKey
		const prevResetKey = prevProps.resetKey
		if (resetKey !== undefined && resetKey !== prevResetKey) {
			this.reset()
		}
	}

	private reset() {
		this.setState({ hasError: false, error: null })
		if (this.props.onReset) {
			this.props.onReset()
		}
	}

	public render() {
		if (!this.state.hasError) {
			return this.props.children
		}

		if (this.props.fallback) {
			return typeof this.props.fallback === "function"
				? this.props.fallback(() => this.reset())
				: this.props.fallback
		}

		const error = this.state.error
		const message = error ? error.message : "Unexpected error"
		const title = this.props.title ? this.props.title : "Something went wrong"
		if (this.props.onReset) {
			return (
				<ErrorBanner
					title={title}
					message={message}
					onRetry={() => this.reset()}
				/>
			)
		}
		return <ErrorBanner title={title} message={message} />
	}
}
